import { useEffect, useRef, useState } from "react";
import { readSavedScrollY, isHomePath, isPageReload } from "./ScrollRestore";

/**
 * Loads a component only when the placeholder enters (or nears) the viewport.
 * Uses dynamic import() — not React.lazy — so nothing downloads until scroll.
 */
export default function ScrollLazySection({
  load,
  render,
  fallback = null,
  rootMargin = "240px 0px",
  minHeight = "120px",
  className = "",
}) {
  const ref = useRef(null);
  const [Component, setComponent] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (Component || failed) return undefined;

    const node = ref.current;
    if (!node) return undefined;

    let cancelled = false;

    const startLoad = () => {
      load()
        .then((mod) => {
          if (cancelled) return;
          const Resolved = mod.default || mod;
          setComponent(() => Resolved);
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        });
    };

    if (isHomePath() && isPageReload()) {
      startLoad();
      return undefined;
    }

    const savedScrollY = readSavedScrollY() ?? 0;
    const nodeTop = node.getBoundingClientRect().top + window.scrollY;
    const restoreViewportBottom = savedScrollY + window.innerHeight + 320;

    if (savedScrollY > 0 && nodeTop < restoreViewportBottom) {
      startLoad();
      return undefined;
    }

    if (typeof IntersectionObserver === "undefined") {
      startLoad();
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        startLoad();
      },
      { rootMargin, threshold: 0.01 }
    );

    observer.observe(node);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [Component, failed, load, rootMargin]);

  if (Component) {
    return <div className={className}>{render(Component)}</div>;
  }

  return (
    <div
      ref={ref}
      className={className}
      style={{ minHeight }}
      aria-hidden={!failed}
    >
      {fallback}
    </div>
  );
}
